import React, { FC, useMemo } from 'react';
import { ColumnsType } from 'antd/lib/table';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useMediaQuery } from 'react-responsive';
import { useGetApplication } from '~/controllers/wizard';
import EditButton from '~/components/views/ShareholdersMultiple/EditButton';
import { hasAtLeastOneWarningField } from '~/utils/softDeclinesFields';
import theme from '~/constants/theme';
import Space from '~/components/Space';
import Table from '~/components/Table';
import { TableCardMobile, TextField } from '~/components';
import { Draft } from '~/types/Draft';
import { KsaShareholders } from '~/types/ksa/KsaShareholders';
import { Application } from '~/types/Application';
import { getLegalEntitiesFormName } from '~/pages/ksa/application/shareholders/components/legalEntities/LegalEntityEdit';
import classNames from 'classnames';

export type LegalEntityRowType = {
  uid?: string;
  legalName?: string;
  number?: string;
  percent?: number;
  onEdit?: () => void;
};

interface Props {
  legalEntities: LegalEntityRowType[];
}

const LegalEntitiesTable: FC<Props> = (props) => {
  const { legalEntities } = props;
  const { t } = useTranslation('ksaApplication');

  const applicationQuery = useGetApplication();

  const businessColumns: ColumnsType<LegalEntityRowType> = useMemo(
    () => [
      {
        title: t('Legal name'),
        dataIndex: 'legalName',
        key: 'legalName',
      },
      {
        title: t('CR number / Entity number'),
        dataIndex: 'number',
        key: 'number',
      },
      {
        title: t('Shares, %'),
        dataIndex: 'percent',
        key: 'shares',
      },
      {
        title: '',
        key: 'action',
        // fixed: 'right',
        render: (_, record) => (
          <div style={actionsStyle}>
            <Space size={16}>
              <EditButton id="legalEntity_EditButton" onClick={record.onEdit} />
            </Space>
          </div>
        ),
      },
    ],
    [t]
  );

  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.sm });

  if (!applicationQuery.data) {
    return null;
  }

  return (
    <Space size={24} direction="vertical">
      {!isEmpty(legalEntities) && (
        <>
          {!isTablet ? (
            <Table
              id="legalEntitiesTable"
              columns={businessColumns}
              dataSource={legalEntities}
              pagination={pagination}
              rowKey="uid"
              rowClassName={(_, index) =>
                classNames({
                  'ant-table-row-warning': hasAnyActiveWarning(applicationQuery.data, index),
                })
              }
            />
          ) : (
            <Space size={12} direction="vertical">
              {legalEntities?.map((item, index) => (
                <TableCardMobile
                  key={item.uid}
                  hasValidationWarnings={hasAnyActiveWarning(applicationQuery.data, index)}
                  onEdit={item.onEdit}
                  fields={getLegalEntitiesCardContent(item)}
                />
              ))}
            </Space>
          )}
        </>
      )}
    </Space>
  );
};

const getLegalEntitiesCardContent = (record: LegalEntityRowType) => {
  const t = i18next.getFixedT(null, 'ksaApplication');
  const { legalName, percent, number } = record;

  return [
    <TextField key="legalName" label={t('Legal Name')}>
      {legalName}
    </TextField>,
    <TextField key="number" label={t('CR number / Entity number')}>
      {number}
    </TextField>,
    <TextField key="shares" label={t('Shares, %')}>
      {percent}
    </TextField>,
  ];
};

export const getLegalEntityData = (
  shareholdersData: Draft<'shareholders'> | undefined,
  openEdit: (uid: string) => void
): LegalEntityRowType[] => {
  const individuals = (shareholdersData?.shareholders as KsaShareholders)?.legalEntities;
  return (
    individuals?.map((item) => {
      return {
        uid: item.uid,
        legalName: item?.legalName,
        number: item?.commercialRegistrationNumber || item?.entityNumber,
        percent: item?.ownershipShare?.percent,
        onEdit: () => item?.uid && openEdit(item.uid),
      };
    }) || []
  );
};

const hasAnyActiveWarning = (application: Application | undefined, index: number) =>
  hasAtLeastOneWarningField(application, getLegalEntitiesFormName(index).join('.'));

const pagination = { hideOnSinglePage: true };

const actionsStyle = { display: 'flex', justifyContent: 'flex-end' };

export default LegalEntitiesTable;
